import React, { useEffect, useState } from 'react';
import { VideoCameraSlashIcon } from '@heroicons/react/24/outline';
import '../release-notes.css';

// A clip that cannot load (unsupported codec, expired url) must say so rather
// than leave a black box. A portrait recording keeps its own ratio, capped in
// height, centred on the dark ground. Hooks live here because the block switch
// below cannot hold them.
const ReleaseNoteVideo = ({ src }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  return (
    <figure
      className={`overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 ${
        failed ? 'bg-slate-50 dark:bg-slate-900' : 'bg-slate-950'
      }`}
    >
      {failed ? (
        <div
          role="status"
          className="flex min-h-[10rem] flex-col items-center justify-center gap-1 px-4 py-8 text-center"
        >
          <VideoCameraSlashIcon className="mb-1 h-6 w-6 text-slate-400 dark:text-slate-500" aria-hidden="true" />
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">This video couldn't be loaded.</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Reload the page to try again.</p>
        </div>
      ) : (
        <video
          src={src}
          controls
          preload="metadata"
          playsInline
          onError={() => setFailed(true)}
          className="mx-auto block h-auto max-h-[75vh] w-auto max-w-full"
        />
      )}
    </figure>
  );
};

// The one body renderer (spec §5.1): detail page and editor preview both draw
// `blocks` in array order. Text nodes only — the API stores plain text, never
// markup, and nothing here interprets it.
const ReleaseNoteBlocks = ({ blocks = [] }) => (
  <>
    {blocks.map((block, index) => {
      const key = block.key || block.id || `${block.type}-${index}`;
      switch (block.type) {
        case 'heading': {
          const Tag = Number(block.level) === 3 ? 'h3' : 'h2';
          return (
            <Tag
              key={key}
              className={
                Tag === 'h3'
                  ? 'rn-display pt-1 text-xl font-semibold text-slate-900 first:pt-0 dark:text-slate-100'
                  : 'rn-display pt-2 text-2xl font-semibold tracking-tight text-slate-900 first:pt-0 dark:text-slate-100'
              }
            >
              {block.text}
            </Tag>
          );
        }
        case 'paragraph':
          return (
            <p
              key={key}
              className="whitespace-pre-line text-base leading-7 text-slate-700 dark:text-slate-300"
            >
              {block.text}
            </p>
          );
        case 'list': {
          const items = (block.items || []).filter((item) => String(item || '').trim());
          if (items.length === 0) return null;
          return (
            <ul
              key={key}
              className="list-disc space-y-2 pl-6 text-base leading-7 text-slate-700 dark:text-slate-300"
            >
              {items.map((item, i) => (
                <li key={`${key}-${i}`}>{item}</li>
              ))}
            </ul>
          );
        }
        case 'image':
          if (!block.mediaurl) return null;
          return (
            <figure
              key={key}
              className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
            >
              <img src={block.mediaurl} alt="" loading="lazy" className="block h-auto w-full" />
            </figure>
          );
        case 'video':
          if (!block.mediaurl) return null;
          return <ReleaseNoteVideo key={key} src={block.mediaurl} />;
        default:
          return null;
      }
    })}
  </>
);

export default ReleaseNoteBlocks;
